import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardBody, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Tag, Search, Plus, RefreshCw, Copy, Pencil, Trash2, TrendingUp, Percent, Power } from 'lucide-react';
import { api } from '../../utils/api';
import toast from 'react-hot-toast';
import { DiscountCodeModal } from '../../components/DiscountCodeModal';

export function DiscountCodesPage() {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setIsSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    totalUsages: 0,
    totalDiscount: 0,
  });

  useEffect(() => {
    loadDiscounts();
  }, [filterStatus, filterType]);

  const loadDiscounts = async () => {
    setLoading(true);
    try {
      const response = await api.getDiscountCodes({
        status: filterStatus,
        type: filterType,
        search: searchQuery,
      });

      if (response.success) {
        setDiscounts(response.discounts);
        calculateStats(response.discounts);
      } else {
        toast.error('Fehler beim Laden der Rabattcodes');
      }
    } catch (error) {
      console.error('Error loading discount codes:', error);
      toast.error('Fehler beim Laden der Rabattcodes');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (discountList) => {
    const total = discountList.length;
    const active = discountList.filter(d => d.status === 'active').length;
    const totalUsages = discountList.reduce((sum, d) => sum + (d.usage_count || 0), 0);
    const totalDiscount = discountList.reduce((sum, d) => sum + (d.total_discount_given || 0), 0);

    setStats({ total, active, totalUsages, totalDiscount });
  };

  const handleSyncWithShopify = async () => {
    setIsSyncing(true);
    try {
      const response = await api.syncDiscountCodes();

      if (response.success) {
        toast.success(`${response.synced} Codes synchronisiert!`);
        await loadDiscounts();
      } else {
        toast.error('Sync fehlgeschlagen');
      }
    } catch (error) {
      console.error('Error syncing:', error);
      toast.error('Sync fehlgeschlagen');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleCreate = () => {
    setSelectedDiscount(null);
    setShowModal(true);
  };

  const handleEdit = (discount) => {
    setSelectedDiscount(discount);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDiscount(null);
  };

  const handleSaveDiscount = () => {
    loadDiscounts();
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code kopiert!');
  };

  const handleToggleStatus = async (id, currentStatus, code) => {
    const newStatus = currentStatus === 'active';
    const action = newStatus ? 'deaktivieren' : 'aktivieren';

    try {
      const response = await api.toggleDiscountCode(id, !newStatus);

      if (response.success) {
        toast.success(`Rabattcode ${newStatus ? 'deaktiviert' : 'aktiviert'}`);
        loadDiscounts();
      } else {
        toast.error(`Fehler beim ${action.charAt(0).toUpperCase() + action.slice(1)}`);
      }
    } catch (error) {
      console.error('Error toggling discount:', error);
      toast.error(`Fehler beim ${action.charAt(0).toUpperCase() + action.slice(1)}`);
    }
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Möchten Sie den Rabattcode "${code}" wirklich löschen?\n\nDer Code wird auch in Shopify gelöscht.`)) {
      return;
    }

    try {
      const response = await api.deleteDiscountCode(id);

      if (response.success) {
        toast.success('Rabattcode gelöscht');
        loadDiscounts();
      } else {
        toast.error('Fehler beim Löschen');
      }
    } catch (error) {
      console.error('Error deleting discount:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateString));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'success',
      inactive: 'secondary',
      expired: 'warning',
      deleted: 'error',
    };

    const labels = {
      active: 'Aktiv',
      inactive: 'Inaktiv',
      expired: 'Abgelaufen',
      deleted: 'Gelöscht',
    };

    return (
      <Badge variant={variants[status] || 'secondary'}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getTypeBadge = (type, value) => {
    if (type === 'percentage') {
      return (
        <div className="flex items-center gap-1">
          <Percent className="w-3 h-3" />
          <span>{value}%</span>
        </div>
      );
    } else if (type === 'fixed_amount') {
      return <span>{formatCurrency(value)}</span>;
    } else if (type === 'free_shipping') {
      return <span>Kostenloser Versand</span>;
    }
    return <span>-</span>;
  };

  const filteredDiscounts = discounts.filter(discount => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        discount.code.toLowerCase().includes(query) ||
        discount.title.toLowerCase().includes(query)
      );
    }
    return true;
  });

  if (loading && discounts.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-500">Lade Rabattcodes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Tag className="w-8 h-8" />
            Rabattcodes
          </h1>
          <p className="text-gray-500 mt-1">
            Verwalten Sie Shopify Rabattcodes und analysieren Sie deren Performance
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Gesamt Codes</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Tag className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Aktive Codes</p>
                <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <Tag className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Gesamte Verwendungen</p>
                <p className="text-2xl font-bold text-purple-600">{stats.totalUsages}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Gewährter Rabatt</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(stats.totalDiscount)}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <Percent className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Filters & Actions */}
      <Card>
        <CardBody>
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Code oder Titel suchen..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">Alle Status</option>
              <option value="active">Aktiv</option>
              <option value="inactive">Inaktiv</option>
              <option value="expired">Abgelaufen</option>
            </select>

            {/* Type Filter */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">Alle Typen</option>
              <option value="percentage">Prozentual</option>
              <option value="fixed_amount">Fester Betrag</option>
              <option value="free_shipping">Kostenloser Versand</option>
            </select>

            {/* Sync Button */}
            <Button
              variant="secondary"
              onClick={handleSyncWithShopify}
              disabled={syncing}
              className="whitespace-nowrap"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Synchronisiere...' : 'Mit Shopify synchronisieren'}
            </Button>

            {/* Create Button */}
            <Button onClick={handleCreate}>
              <Plus className="w-4 h-4" />
              Neuer Code
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Discount Codes Table */}
      <Card>
        <CardHeader>
          <CardTitle>Rabattcodes ({filteredDiscounts.length})</CardTitle>
        </CardHeader>
        <CardBody>
          {filteredDiscounts.length === 0 ? (
            <div className="text-center py-12">
              <Tag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">
                {searchQuery ? 'Keine Rabattcodes gefunden' : 'Noch keine Rabattcodes vorhanden'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Titel</TableHead>
                    <TableHead>Typ & Wert</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Verwendungen</TableHead>
                    <TableHead>Gültig bis</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDiscounts.map((discount) => (
                    <TableRow key={discount.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <code className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">
                            {discount.code}
                          </code>
                          <button
                            onClick={() => handleCopyCode(discount.code)}
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                            title="Code kopieren"
                          >
                            <Copy className="w-4 h-4 text-gray-400" />
                          </button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs">
                          <p className="font-medium text-gray-900 truncate">{discount.title}</p>
                          {discount.description && (
                            <p className="text-sm text-gray-500 truncate">{discount.description}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getTypeBadge(discount.discount_type, discount.value)}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(discount.status)}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <span className="font-medium">{discount.usage_count || 0}</span>
                          {discount.usage_limit && (
                            <span className="text-gray-500"> / {discount.usage_limit}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {formatDate(discount.ends_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleToggleStatus(discount.id, discount.status, discount.code)}
                            className={`p-2 hover:bg-gray-100 rounded transition-colors ${
                              discount.status === 'active' ? 'text-green-600' : 'text-gray-400'
                            }`}
                            title={discount.status === 'active' ? 'Deaktivieren' : 'Aktivieren'}
                          >
                            <Power className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => toast('Analytics coming soon...')}
                            className="p-2 hover:bg-gray-100 rounded transition-colors"
                            title="Analytics anzeigen"
                          >
                            <TrendingUp className="w-4 h-4 text-gray-600" />
                          </button>
                          <button
                            onClick={() => handleEdit(discount)}
                            className="p-2 hover:bg-gray-100 rounded transition-colors"
                            title="Bearbeiten"
                          >
                            <Pencil className="w-4 h-4 text-gray-600" />
                          </button>
                          <button
                            onClick={() => handleDelete(discount.id, discount.code)}
                            className="p-2 hover:bg-red-50 rounded transition-colors"
                            title="Löschen"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Discount Code Modal */}
      {showModal && (
        <DiscountCodeModal
          discount={selectedDiscount}
          onClose={handleCloseModal}
          onSave={handleSaveDiscount}
        />
      )}
    </div>
  );
}
